import { useState } from 'react';
import {
	FormHelperText,
	Typography,
	TextField,
	Button,
	Checkbox,
	FormControlLabel,
	Box,
	CircularProgress
} from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import { GoogleLogin } from '@react-oauth/google';
import { API_UserLogin, API_UserLogout } from '../Assets/ApiUrlContainer';
import { Link } from 'react-router-dom'; // Import Link
import { default as axios } from '../axiosConfig';

const LoginPage = ({ onGoogleLoginSuccess, onCustomLoginSuccess, googleIsLoading }) => {
	const [showPassword, setShowPassword] = useState(false);
	const [loading, setLoading] = useState(false);
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [isLoggedIn, setIsLoggedIn] = useState(false); // Track login state

	const [emailError, setEmailError] = useState(false);
	const [passwordError, setPasswordError] = useState(false);

	const [recaptchaToken, setRecaptchaToken] = useState(null); // Store reCAPTCHA token
	const [captchaError, setCaptchaError] = useState(false);
	const [serverError, setServerError] = useState('');

	// Google OAuth setup
	const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

	const handleSubmit = async (e) => {
		e.preventDefault();
		setEmailError(false);
		setPasswordError(false);
		setCaptchaError(false);
		setServerError('');

		let hasError = false;

		if (!email.includes('@')) {
			setEmailError(true);
			hasError = true;
		}

		if (password.length === 0) {
			setPasswordError(true);
			hasError = true;
		}

		if (!recaptchaToken) {
			setCaptchaError(true);
			hasError = true;
		}

		if (hasError) {
			return;
		}

		try {
			setLoading(true);
			const response = await axios.post(API_UserLogin, { email, password, captchaToken: recaptchaToken });
			setLoading(false);

			if (response.data.message === 'Login successful') {
				setIsLoggedIn(true); // Set login state
				onCustomLoginSuccess({
					Email: email,
					Role: response.data.Role,
					FirstName: response.data.FirstName,
					LastName: response.data.LastName,
					Id: response.data.Id,
					CandidateId: response.data.CandidateId
				});
			} else {
				setServerError('Unexpected response from server.');
			}
		} catch (error) {
			if (error.response && error.response.data.error) {
				setServerError(error.response.data.error);
			} else {
				setServerError('An error occurred during login.');
			}
		} finally {
			setLoading(false);
		}
	};

	const handleLogout = async () => {
		console.log('Logout initiated...');
		try {
			const response = await fetch(API_UserLogout, {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
			});

			if (!response.ok) {
				throw new Error(`Logout failed: ${response.status}`);
			}

			const data = await response.json();
			console.log('Logout response:', data);
			window.location.href = '/';
		} catch (error) {
			console.error('Error during logout:', error);
		}
	};

	const handleRecaptchaChange = (token) => {
		setRecaptchaToken(token);
		if (token) setCaptchaError(false);
	};

	return (
		<Box
			className='loginContainer'
			sx={{
				height: '100vh',
				width: '100vw',
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'flex-start',
				alignItems: 'center',
				backgroundColor: '#FAFAFA',
				paddingTop: '32px',
			}}
		>
			<Box sx={{ maxWidth: '580px', width: '100%', textAlign: 'center' }}>
				<Typography
					variant='h4'
					component='h2'
					align='left'
					mb={1}
					sx={{
						fontFamily: 'var(--fontFamily)',
						fontSize: '2.125rem',
						fontWeight: 400,
						color: '#000000DE',
						textAlign: 'left',
					}}
				>
					{isLoggedIn ? 'Welcome Back!' : 'Welcome'}
				</Typography>
				{!isLoggedIn ? (
					<form onSubmit={handleSubmit} style={{ textAlign: 'left' }} noValidate>
						<TextField
							label='Email'
							variant='filled'
							size='medium'
							margin='normal'
							required
							value={email}
							onChange={(e) => {
								setEmail(e.target.value);
								setEmailError(false);
							}}
							error={emailError}
							disabled={loading}
							helperText={emailError && 'Enter a valid email address'}
							fullWidth
							sx={{ marginBottom: '16px' }}
						/>
						{/* {emailError && <FormHelperText error>Enter a valid email address</FormHelperText>} */}
						<TextField
							label='Password'
							variant='filled'
							size='medium'
							margin='normal'
							required
							type={showPassword ? 'text' : 'password'}
							value={password}
							onChange={(e) => {
								setPassword(e.target.value);
								setPasswordError(false);
							}}
							error={passwordError}
							disabled={loading}
							fullWidth
							helperText={passwordError && 'Enter your password'}
							sx={{ marginBottom: '8px' }}
						/>
						<Box sx={{ textAlign: 'right', marginBottom: '16px' }}>
							<Typography
								variant='body2'
								component='a'
								href='/forgot-password'
								sx={{
									color: '#673AB7',
									textDecoration: 'none',
									cursor: 'pointer',
									'&:hover': { textDecoration: 'underline' },
								}}
							>
								Forgot Password?
							</Typography>
						</Box>
						<FormControlLabel
							control={
								<Checkbox checked={showPassword} onChange={() => setShowPassword(!showPassword)} />
							}
							label='Show password'
							sx={{ marginBottom: '16px', textAlign: 'left' }}
						/>
						<ReCAPTCHA sitekey={siteKey} onChange={handleRecaptchaChange} />
						{captchaError && <FormHelperText error>Please complete the CAPTCHA</FormHelperText>}
						<Button
							type='submit'
							fullWidth
							variant='contained'
							disabled={loading}
							sx={{ marginTop: '16px', backgroundColor: '#673AB7' }}
						>
							{loading ? <CircularProgress size={24} color='inherit' /> : 'LOG IN'}
						</Button>

						{/* Register Button (Added Below Login Button) */}
						<Typography
							variant='body2'
							component={Link}
							to='/register'
							sx={{
								display: 'block',
								marginTop: '12px',
								textAlign: 'center',
								color: '#673AB7',
								textDecoration: 'none',
								cursor: 'pointer',
								'&:hover': { textDecoration: 'underline' },
							}}
						>
							Don't have an account? Register
						</Typography>

						{serverError && <FormHelperText error>{serverError}</FormHelperText>}

						<Typography sx={{ marginY: '16px', color: '#00000099', fontSize: '14px', textAlign: 'center' }}>
							OR
						</Typography>
						<div className='googleLoginBtn'>
							{googleIsLoading ? (
								<CircularProgress size={24} color='inherit' />
							) : (
								<GoogleLogin
									useOneTap
									theme='filled_blue'
									width='400'
									onSuccess={(credentialResponse) => {
										onGoogleLoginSuccess(credentialResponse.credential);
									}}
								/>
							)}

						</div>
					</form>
				) : (
					<Button
						fullWidth
						variant='contained'
						onClick={handleLogout}
						sx={{ marginTop: '16px', backgroundColor: '#D32F2F' }}
					>
						LOGOUT
					</Button>
				)}
			</Box>
		</Box>
	);
};

export default LoginPage;
